import type { Node, Edge } from "@xyflow/react";
import type { IdeaNodeData, InfoPort, DbSchema, DbTable } from "./types";

// ─── Internal clean types ──────────────────────────────────────────────────────

interface CleanPort {
  name: string;
  type: string;
  direction: "input" | "output";
  description: string;
  isRequired: boolean;
  dbMapping?: string;
}

interface CleanNode {
  id: string;
  title: string;
  actionType: string;
  description: string;
  inputs: CleanPort[];
  outputs: CleanPort[];
  upstreamNodes: string[];
  downstreamNodes: string[];
  specification: string;
}

// ─── ContextEngineer ───────────────────────────────────────────────────────────

export class ContextEngineer {
  private cleanNodes: Map<string, CleanNode> = new Map();
  private dbSchemaString: string = "";

  constructor(
    private readonly nodes: Node<IdeaNodeData>[],
    private readonly edges: Edge[],
    private readonly dbSchema: DbSchema,
  ) {
    this.parseDbSchema();
    this.buildLogicalGraph();
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  private parseDbSchema() {
    if (!this.dbSchema.tables.length) return;

    this.dbSchemaString = this.dbSchema.tables
      .map((table: DbTable) => {
        const columns = table.columns
          .map(
            (c) =>
              `  - ${c.name} (${c.type})${c.nullable ? " [optional]" : ""}${c.description ? ` — ${c.description}` : ""}`,
          )
          .join("\n");
        return `Table: ${table.name}\n${columns}`;
      })
      .join("\n\n");
  }

  private resolveDbMapping(dbField?: {
    tableId: string;
    columnId: string;
  }): string | undefined {
    if (!dbField) return undefined;
    const table = this.dbSchema.tables.find((t) => t.id === dbField.tableId);
    if (!table) return undefined;
    const column = table.columns.find((c) => c.id === dbField.columnId);
    if (!column) return undefined;
    return `${table.name}.${column.name}`;
  }

  private portToClean(p: InfoPort): CleanPort {
    return {
      name: p.name,
      // prefer primitiveType for primitives, fall back to format (list/object)
      type: p.primitiveType ?? p.format,
      direction: p.direction,
      description: p.description ?? "",
      isRequired: p.required,
      dbMapping: this.resolveDbMapping(p.dbField),
    };
  }

  private buildLogicalGraph() {
    for (const rawNode of this.nodes) {
      const d = rawNode.data;
      const cleanPorts = (d.ports ?? []).map((p) => this.portToClean(p));

      const node: CleanNode = {
        id: rawNode.id,
        title: d.title || "(Kein Titel)",
        actionType:
          d.nodeKind === "action"
            ? (d.actionPayload?.subtype ?? "action")
            : d.nodeKind,
        specification: JSON.stringify(
          {
            signal: d.signal,
            state: d.state,
            action: d.actionPayload,
            notes: d.notes,
            testInput: d.testInput,
            expectedOutput: d.expectedOutput,
          },
          null,
          2,
        ),
        description: d.description ?? "",
        inputs: cleanPorts.filter((p) => p.direction === "input"),
        outputs: cleanPorts.filter((p) => p.direction === "output"),
        upstreamNodes: [],
        downstreamNodes: [],
      };

      this.cleanNodes.set(node.id, node);
    }

    for (const edge of this.edges) {
      const source = this.cleanNodes.get(edge.source);
      const target = this.cleanNodes.get(edge.target);
      if (!source || !target) continue;
      if (!source.downstreamNodes.includes(target.title))
        source.downstreamNodes.push(target.title);
      if (!target.upstreamNodes.includes(source.title))
        target.upstreamNodes.push(source.title);
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  /** All node IDs in the graph (in insertion order). */
  get nodeIds(): string[] {
    return [...this.cleanNodes.keys()];
  }

  /** Title of a specific node. */
  getNodeTitle(nodeId: string): string {
    return this.cleanNodes.get(nodeId)?.title ?? nodeId;
  }

  /**
   * High-level pipeline map — use this as the system context for every prompt.
   */
  getHighLevelOverview(): string {
    let overview = `PIPELINE OVERVIEW:\n`;
    overview += `Diese Pipeline besteht aus folgenden verbundenen Schritten:\n\n`;

    for (const node of this.cleanNodes.values()) {
      overview += `- [${node.title}] (${node.actionType})\n`;
      overview += `   ${node.description}\n   Spezifikation: ${node.specification}\n`;
      if (node.upstreamNodes.length > 0)
        overview += `   <- Input kommt von: ${node.upstreamNodes.join(", ")}\n`;
      if (node.downstreamNodes.length > 0)
        overview += `   -> Output geht an: ${node.downstreamNodes.join(", ")}\n`;
      overview += "\n";
    }

    if (this.dbSchemaString) {
      overview += `DATENBANK SCHEMA:\n${this.dbSchemaString}\n`;
    }

    return overview;
  }

  /**
   * Implementation prompt for a single node.
   */
  getPromptForNode(nodeId: string): string {
    const node = this.cleanNodes.get(nodeId);
    if (!node) throw new Error(`Node "${nodeId}" nicht gefunden.`);

    const formatPorts = (ports: CleanPort[]) => {
      if (ports.length === 0) return "Keine";
      return ports
        .map((p) => {
          let s = `- ${p.name} (${p.type})${p.isRequired ? " [required]" : ""}`;
          if (p.description) s += `\n    Beschreibung: ${p.description}`;
          if (p.dbMapping) s += `\n    Speichern in: ${p.dbMapping}`;
          return s;
        })
        .join("\n");
    };

    return `SYSTEM-KONTEXT:
Du bist ein Backend-Entwickler und implementierst eine Node-basierte Pipeline.
${this.getHighLevelOverview()}
AKTUELLER TASK:
Implementiere die Logik für den Node "${node.title}".

SPEZIFIKATION FÜR DIESEN NODE:
- Typ der Ausführung: ${node.actionType}
- Beschreibung: ${node.description || "Keine spezifische Beschreibung vorhanden."}

DETAILS (Signal, Zustand, Prompts, Anforderungen und Beispiele):
${node.specification}

INPUTS:
${formatPorts(node.inputs)}

OUTPUTS:
${formatPorts(node.outputs)}

ANFORDERUNG:
Schreibe eine asynchrone Funktion in TypeScript für diesen Schritt.
Gehe davon aus, dass die Inputs als Argumente übergeben werden.
Das Ergebnis muss ein Objekt zurückgeben, das exakt den OUTPUTS entspricht.
Implementiere sauberes Error-Handling.`;
  }
}
